/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/

import * as _ from 'lodash';

import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { AppUtilityService } from '../../services/app-utility.service';
import { AuthenticatedUser } from './../../model/authenticated-user.model';
import { ConfigService } from './../../services/config.service';
import { LmsLoaderService } from '../../../core/services/lms-loader.service';
import { MessageDetails } from '../../model/message-details.model';
import { MessageHandlerService } from './../../services/message-handler.service';
import { SelectItem } from 'primeng/api/selectitem';
import { StaticDataStoreService } from './../../services/static-datastore.service';
import { UserConfigService } from './../../services/user-config.service';
import { UserPrefWindow } from '../../model/user-pref-window-model';
import { UserProfile } from '../../model/user-profile.model';
import { UserProfileService } from './../../services/user-profile.service';

/**
 * @export
 * @class LmsUserProfileComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'lms-user-profile',
  templateUrl: './lms-user-profile.component.html',
  styleUrls: ['./lms-user-profile.component.scss']
})
export class LmsUserProfileComponent implements OnInit, OnDestroy {

  @Output() isShowUserProfile = new EventEmitter<boolean>();
  private isSubmitPossible = true;
  private userConfigData;
  private configData: any;
  alertValue = 'NON'; // SYS, DES, NON
  private userProfile: UserProfile;
  selectedDeskName: string;
  userPrefWindowArr: Array<UserPrefWindow>;
  deskList: Array<SelectItem> = [];
  @Input() initWindowCount;
  private preAlertValue;
  user: AuthenticatedUser;
  @Output() popupErrorMessage = new EventEmitter<string>();
  @Output() errorDialog = new EventEmitter<boolean>();
  private MIN_WINDOW_COUNT = 5;
  private MAX_WINDOW_COUNT = 10;
  tabLevel: number;
  helpUrl: string;

  /**
   * Creates an instance of LmsUserProfileComponent.
   * @param {UserConfigService} userConfigService
   * @memberOf LmsUserProfileComponent
   */
  constructor(private userConfigService: UserConfigService,
    private userProfileService: UserProfileService,
    private staticDataStoreService: StaticDataStoreService,
    private configService: ConfigService,
    private messageHandlerService: MessageHandlerService,
    private lmsLoaderService: LmsLoaderService,
    private appUtilityService: AppUtilityService) {
    this.tabLevel = this.configService.getConfigData().tabIndex.dialogBox;
  }


  /**
   * @memberOf LmsUserProfileComponent
   */
  ngOnInit() {
    this.userProfile = this.staticDataStoreService.getValue('userProfile');
    this.user = this.staticDataStoreService.getValue('user');
    this.userPrefWindowArr = [];
    this.userConfigData = this.userConfigService.getUserConfigData();
    this.configData = this.configService.getConfigData();

    this.loadDeskList();
    this.loadWindow();
    this.retrieveUserPrefileData();
    this.helpUrl = this.appUtilityService.getHelpUrl('User-Profile', 'Default');
    if (!this.helpUrl || this.helpUrl.length < 10) {
      this.helpUrl = 'https://wiki.csx.com/display/HC/User+Profile';
    }
  }

  /**
   * Retrieve user profile data from static datastore.
   * @memberof LmsUserProfileComponent
   */
  retrieveUserPrefileData() {
    if (this.userProfile) {
      if (this.userProfile.alertType) {
        this.preAlertValue = this.userProfile.alertType;
        this.alertValue = this.userProfile.alertType;
        if (this.alertValue && this.alertValue === 'DES') {
          if (this.userProfile.alertDesk) {
            if (('Select Desk' === this.userProfile.alertDesk) && this.deskList && this.deskList.length > 1) {
              this.selectedDeskName = this.deskList[1].value;
            } else {
              this.selectedDeskName = this.userProfile.alertDesk;
            }
          } else if (this.deskList && this.deskList.length > 1) {
            this.selectedDeskName = this.deskList[1].value;
          }
        } else {
          this.selectedDeskName = '';
        }
      }
    }
  }
  /**
   * Invoked when click on apply button.
   * @memberOf LmsUserProfileComponent
   */
  submitData() {
    this.lmsLoaderService.showLoader();
    this.isSubmitPossible = true;

    let userPrefWindowArr = this.userPrefWindowArr.filter((userPrefWindow) => {
      return (userPrefWindow.windowName || (!userPrefWindow.windowName && userPrefWindow.id));
    });

    this.userPrefWindowArr = this.checkMenmonicValidation(this.userPrefWindowArr);

    if (!this.isSubmitPossible) {
      this.lmsLoaderService.hideLoader();
      return;
    } else {
      userPrefWindowArr = this.buildUserPrefRequest(userPrefWindowArr);
    }
    this.performAddUpdateOpr(userPrefWindowArr);
  }

  /**
   * Perform add/update operations for user setting.
   * @param {Array<UserPrefWindow>} userPrefWindowArr
   * @memberof LmsUserProfileComponent
   */
  performAddUpdateOpr(userPrefWindowArr: Array<UserPrefWindow>) {
    let userPrefWindowCloneArr = this.cloneWithSpecificProp(this.userPrefWindowArr);

    userPrefWindowCloneArr = userPrefWindowCloneArr.filter((userPrefWindow) => {
      return (userPrefWindow.windowName || (!userPrefWindow.windowName && userPrefWindow.id));
    });

    const locoStatusArr = userPrefWindowCloneArr.filter((userPrefWindow) => {
      return (userPrefWindow.windowName === 'Locomotive-Status Data');
    });
    if (locoStatusArr && locoStatusArr.length < 2) {
      let alertDesk = '';
      if (this.alertValue === 'DES') {
        alertDesk = this.selectedDeskName;
      }
      this.userProfile = {
        alertType: this.alertValue,
        alertDesk: alertDesk,
        userId: this.user.racfId,
        userPrefWindowList: userPrefWindowArr
      };
      if (this.preAlertValue) {
        this.updateUserSetting();
      } else {
        this.addUserSetting();
      }
    } else {
      this.lmsLoaderService.hideLoader();
      this.errorDialog.emit(true);
      this.popupErrorMessage.emit(this.configData.messages.defaultWindowExist);
    }
  }

  /**
   * Build User Preferences Request array.
   * @param {Array<UserPrefWindow>} userPrefWindowArr
   * @returns {Array<UserPrefWindow>}
   * @memberof LmsUserProfileComponent
   */
  buildUserPrefRequest(userPrefWindowArr: Array<UserPrefWindow>): Array<UserPrefWindow> {
    const newUserPrefArray = [];
    if (userPrefWindowArr && userPrefWindowArr.length > 0) {
      userPrefWindowArr.forEach((pref: UserPrefWindow) => {
        const terminalMnemonic = (pref && pref.terminalMnemonic) ? pref.terminalMnemonic.toUpperCase() : '';
        const newPref = {
          id: pref.id,
          windowName: pref.windowName,
          terminalMnemonic: terminalMnemonic,
          noThroughTrains: pref.noThroughTrains,
          deskName: pref.deskName
        };
        newUserPrefArray.push(newPref);
      });
    }
    userPrefWindowArr = newUserPrefArray;
    return userPrefWindowArr;
  }

  /**
   * Update user setting for requested parameter.
   * @memberof LmsUserProfileComponent
   */
  updateUserSetting() {
    this.userProfileService.updateSetting(this.userProfile).subscribe(
      (data) => {
        this.lmsLoaderService.hideLoader();
        this.isShowUserProfile.emit(true);
        this.messageHandlerService.handleMessage(<MessageDetails>({
          type: this.configData.message.type.client,
          level: this.configData.message.level.success,
          target: this.configData.message.target.page,
          message: this.configData.messages.userSettingSuccess
        }));
      }, error => {
        this.lmsLoaderService.hideLoader();
        this.errorDialog.emit(true);
        this.popupErrorMessage.emit(this.appUtilityService.getHttpErroMessage(error, false));
      }
    );
  }

  /**
   * Add user setting for requested parameter.
   * @memberof LmsUserProfileComponent
   */
  addUserSetting() {
    this.userProfileService.addSetting(this.userProfile).subscribe(
      (data) => {
        this.lmsLoaderService.hideLoader();
        this.isShowUserProfile.emit(true);
        this.messageHandlerService.handleMessage(<MessageDetails>({
          type: this.configData.message.type.client,
          level: this.configData.message.level.success,
          target: this.configData.message.target.page,
          message: this.configData.messages.userSettingSuccess
        }));
      }, error => {
        this.lmsLoaderService.hideLoader();
        this.errorDialog.emit(true);
        this.popupErrorMessage.emit(this.appUtilityService.getHttpErroMessage(error, false));
      }
    );
  }

  /**
   * Invoked when click on add more button.
   * @memberOf LmsUserProfileComponent
   */
  addMoreWindow() {
    this.initWindowCount = this.MAX_WINDOW_COUNT;
    this.loadAddMoreWindow();
    setTimeout(() => {
      this.appUtilityService.setFocusToFirstElement('dialogUserPrefWindow');
    }, 20);
  }

  /**
   * Invoked for load initial windows.
   * @memberOf LmsUserProfileComponent
   */
  loadWindow() {
    this.userPrefWindowArr = [];
    if (this.userProfile && this.userProfile.userPrefWindowList) {
      if (this.userProfile.userPrefWindowList.length > 5) {
        this.initWindowCount = this.MAX_WINDOW_COUNT;
      } else {
        this.initWindowCount = this.MIN_WINDOW_COUNT;
      }
      this.initWindowCount = this.initWindowCount - this.userProfile.userPrefWindowList.length;
      this.userPrefWindowArr = this.userProfile.userPrefWindowList;
    }

    for (let i = 0; i < this.initWindowCount; i++) {
      const userWindow = {
        id: '',
        windowName: '',
        terminalMnemonic: '',
        noThroughTrains: '',
        deskName: ''
      };
      this.userPrefWindowArr.push(userWindow);
    }
  }

  /**
   * Invoked for add more window.
   * @memberOf LmsUserProfileComponent
   */
  loadAddMoreWindow() {
    for (let i = this.MIN_WINDOW_COUNT; i < this.initWindowCount; i++) {
      const userWindow = {
        id: '',
        windowName: '',
        terminalMnemonic: '',
        noThroughTrains: '',
        deskName: ''
      };
      this.userPrefWindowArr.push(userWindow);
    }
  }

  /**
   * Triggered when close the popup.
   * @memberOf LmsUserProfileComponent
   */
  closeUserProfilePopup() {
    this.isShowUserProfile.emit(true);
  }

  /**
   * Destroy the state of lifecycle hook
   * reset the window count
   * @memberOf LmsUserProfileComponent
   */
  ngOnDestroy() {
    this.initWindowCount = this.MIN_WINDOW_COUNT;
  }

  /**
   * Remove Dublicate object if properties matched.
   * @param {any} keyFn
   * @param {any} array
   * @returns
   * @memberOf LmsUserProfileComponent
   */
  removeDuplicatesBy(keyFn, array) {
    const mySet = new Set();
    return array.filter(function (x) {
      const key = keyFn(x), isNew = !mySet.has(key);
      if (isNew) { mySet.add(key); };
      return isNew;
    });
  }

  /**
   * Clone user UserPrefWindow object for error hasndling for same window.
   * @param {Array<UserPrefWindow>} userPrefWindowArr
   * @returns {Array<UserPrefWindow>}
   * @memberOf LmsUserProfileComponent
   */
  cloneWithSpecificProp(userPrefWindowArr: Array<UserPrefWindow>): Array<UserPrefWindow> {
    const userPrefWindowCloneArr: Array<UserPrefWindow> = [];
    for (let i = 0; i < userPrefWindowArr.length; i++) {
      let wName = '';
      let tMnemonic = '';
      if (userPrefWindowArr[i].windowName) {
        wName = userPrefWindowArr[i].windowName;
      }
      if (userPrefWindowArr[i].terminalMnemonic) {
        tMnemonic = userPrefWindowArr[i].terminalMnemonic;
      }
      const userPrefWindow = {
        windowName: wName,
        terminalMnemonic: tMnemonic
      };
      userPrefWindowCloneArr.push(userPrefWindow);
    }
    return userPrefWindowCloneArr;
  }

  /**
   * Check for mnemonic validation.
   * @param {Array<UserPrefWindow>} userPrefWindowArr
   * @returns {Array<UserPrefWindow>}
   * @memberOf LmsUserProfileComponent
   */
  checkMenmonicValidation(userPrefWindowArr: Array<UserPrefWindow>): Array<UserPrefWindow> {
    const newUserPrefWindowArr: Array<UserPrefWindow> = [];
    for (let i = 0; i < userPrefWindowArr.length; i++) {
      newUserPrefWindowArr.push(this.validateUserPref(userPrefWindowArr[i]));
    }
    return newUserPrefWindowArr;
  }


  /**
   * Check for valid user preferences.
   * @param {UserPrefWindow} userPrefWindow
   * @returns
   * @memberOf LmsUserProfileComponent
   */
  validateUserPref(userPrefWindow: UserPrefWindow) {
    let emitValue = '';
    let value = '';
    let originalValue = '';
    if (userPrefWindow && userPrefWindow.terminalMnemonic) {
      // value = this.rightPad(userPrefWindow.terminalMnemonic, ' ', 6).toUpperCase();
      value = userPrefWindow.terminalMnemonic.trim();
      const commonopsPicklist = this.staticDataStoreService.getValue('commonopsPicklist');
      if (commonopsPicklist) {
        const data = commonopsPicklist.mapOfMnemonis ? commonopsPicklist.mapOfMnemonis : null;
        for (const key in data) {
          if (data.hasOwnProperty(key) && value === data[key]) {
            originalValue = data[key];
            emitValue = 'FOUND';
          }
        }
      }
      if (value && (value.trim().toUpperCase() === 'DELETE' || value.trim().toUpperCase() === 'OFFLN'
        || value.trim().toUpperCase() === 'NEW' || value.trim().toUpperCase() === 'PARKED')) {
        emitValue = 'FOUND';
        originalValue = value.trim().toUpperCase();
      }
      if (!emitValue) {
        userPrefWindow.isMnemonicError = true;
        userPrefWindow.terminalMnemonic = originalValue;
        this.isSubmitPossible = false;
      } else {
        userPrefWindow.isMnemonicError = false;
      }
    }

    return userPrefWindow;
  }

  /**
   * Appy right padding to text.
   * @private
   * @param {any} str
   * @param {any} padString
   * @param {any} max
   * @returns {string}
   * @memberOf LmsUserProfileComponent
   */
  private rightPad(str, padString, max): string {
    str = str.toString();
    return str.length < max ? this.rightPad(str + padString, padString, max) : str;
  }

  /**
   * Update alert type.
   * @memberof LmsUserProfileComponent
   */
  public updateAlertType() {
    this.loadDeskList();
  }

  /**
   * Load desk list from common picklist.
   * @memberof LmsUserProfileComponent
   */
  loadDeskList() {
    const managerDesk = this.staticDataStoreService.getValue('commonopsPicklist');
    if (managerDesk && managerDesk.masterData) {
      this.deskList = [];
      for (let i = 0; i < managerDesk.masterData.zoneList.length; i++) {
        this.deskList.push({
          label: managerDesk.masterData.zoneList[i].zoneCode + ' ' + managerDesk.masterData.zoneList[i].zoneDescription,
          value: managerDesk.masterData.zoneList[i].zoneCode
        });

      }
    }
    if (this.deskList && this.deskList.length > 0) {
      if (!this.selectedDeskName) {
        this.selectedDeskName = this.deskList[0].value;
      }
    }
  }

  showHelpUrl() {
    window.open(
      this.helpUrl,
      '_blank'
    );
  }
}
